<template>
  <div class="container" v-if="$can(['SuperAdmin', 'manager'])">
    <div class="box-title" :style="{marginTop: '-15px'}">
      <h4>
        <i class="fal fa-analytics"></i>
        {{ $t("externalShipments.dashboard_title") }}
        <span class="countTotal" v-if="dashboard">({{ dashboard.from }} {{ $t("externalShipments.to_date_joiner") }} {{ dashboard.to }})</span>
        <span class="btn-add">
          <div class="columns">
            <div class="field" :style="{marginRight: '5px'}">
              <vs-button
                  ref="button" flat
                  @click="ExportReport()"
                  class="form-btn-loading"
                  :loading="exporting">
                Export excel
              </vs-button>
            </div>
          </div>
        </span>
      </h4>
    </div>

    <!-- date range -->
    <div class="box" style="margin-bottom: 12px;margin-top: -12px;">
      <div class="columns">
        <div class="column is-2">
          <div class="control field">
            <label class="label">{{ $t("ລາຍການOrderພັດສະດຸ.ຈາກວັນທີ") }}:</label>
            <date-picker
                format="DD/MM/YYYY"
                value-type="YYYY-MM-DD"
                type="date"
                v-model="fromDate"
                :disabled-date="disabledAfterToday"
                :clearable="false"
                @change="FetchDashboard()"
            ></date-picker>
          </div>
        </div>
        <div class="column is-2">
          <div class="control field">
            <label class="label">{{ $t("ລາຍການOrderພັດສະດຸ.ຫາວັນທີ") }}:</label>
            <date-picker
                format="DD/MM/YYYY"
                value-type="YYYY-MM-DD"
                type="date"
                v-model="toDate"
                :disabled-date="disabledAfterToday"
                :clearable="false"
                @change="FetchDashboard()"
            ></date-picker>
          </div>
        </div>
        <div class="column is-2">
          <div class="control field">
            <label class="label">&nbsp;</label>
            <vs-button flat @click="GoToOrders()">
              <i class="fal fa-list" style="margin-right: 6px"></i> {{ $t("externalShipments.view_orders") }}
            </vs-button>
          </div>
        </div>
      </div>
    </div>

    <div v-if="dashboard">
      <!-- KPI tiles (Bulma cards — same pattern as List-Dashboard) -->
      <div class="columns">
        <div class="column is-3">
          <div class="card">
            <div class="card-content">
              <div class="card-parent">
                <div class="card-left-section">
                  <div class="card-box"><i class="fal fa-boxes"></i></div>
                </div>
                <div class="card-right-section">
                  <h2>{{ $t("externalShipments.total_orders") }}</h2>
                  <h1>{{ FormatNumber(dashboard.totals.orders) }}</h1>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div class="column is-3">
          <div class="card">
            <div class="card-content">
              <div class="card-parent">
                <div class="card-left-section">
                  <div class="card-box"><i class="fal fa-hand-holding-usd"></i></div>
                </div>
                <div class="card-right-section">
                  <h2>{{ $t("externalShipments.hal_service_fee_revenue") }}</h2>
                  <h1>{{ FormatPrice(dashboard.totals.service_fee) }}</h1>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div class="column is-3">
          <div class="card">
            <div class="card-content">
              <div class="card-parent">
                <div class="card-left-section">
                  <div class="card-box"><i class="fal fa-truck-loading"></i></div>
                </div>
                <div class="card-right-section">
                  <h2>{{ $t("externalShipments.provider_freight") }}</h2>
                  <h1>{{ FormatPrice(dashboard.totals.provider_freight) }}</h1>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div class="column is-3">
          <div class="card">
            <div class="card-content">
              <div class="card-parent">
                <div class="card-left-section">
                  <div class="card-box"><i class="fal fa-money-bill-wave"></i></div>
                </div>
                <div class="card-right-section">
                  <h2>{{ $t("externalShipments.cod_orders_title", { n: FormatNumber(dashboard.totals.cod_orders) }) }}</h2>
                  <h1>{{ FormatPrice(dashboard.totals.cod_amount) }}</h1>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- status-group tiles -->
      <div class="columns">
        <div class="column is-3" v-for="group in statusTiles" :key="group.key">
          <div class="card">
            <div class="card-content">
              <div class="card-parent">
                <div class="card-left-section">
                  <div class="card-box" :style="{background: group.color}">
                    <i :class="group.icon"></i>
                  </div>
                </div>
                <div class="card-right-section">
                  <h2>{{ group.label }}</h2>
                  <h1>{{ FormatNumber(statusCount(group.key)) }}</h1>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- daily charts -->
      <div class="columns">
        <div class="column is-6">
          <div class="box">
            <h4 style="margin-bottom: 8px"><i class="fal fa-chart-bar"></i> {{ $t("externalShipments.orders_per_day") }}</h4>
            <external-daily-bar-chart
                :labels="dailyLabels"
                :values="dailyOrders"
                :label="$t('externalShipments.order_count')"
                color="#E11F26"
                :unit="$t('externalShipments.orders')"
                :style="{minHeight: '320px'}"
            />
          </div>
        </div>
        <div class="column is-6">
          <div class="box">
            <h4 style="margin-bottom: 8px"><i class="fal fa-chart-line"></i> {{ $t("externalShipments.hal_service_fee_per_day") }}</h4>
            <external-daily-bar-chart
                :labels="dailyLabels"
                :values="dailyFees"
                :label="$t('externalShipments.service_fee')"
                color="#2EA8D9"
                :unit="$t('common.ກີບ')"
                :style="{minHeight: '320px'}"
            />
          </div>
        </div>
      </div>

      <!-- breakdown tables -->
      <div class="columns">
        <div class="column is-6">
          <div class="box">
            <h4 style="margin-bottom: 8px"><i class="fal fa-store"></i> {{ $t("externalShipments.top_branches") }}</h4>
            <vs-table>
              <template #thead>
                <vs-tr>
                  <vs-th>{{ $t("common.ສາຂາ") }}</vs-th>
                  <vs-th>{{ $t("externalShipments.orders") }}</vs-th>
                  <vs-th>{{ $t("ລາຍການOrderພັດສະດຸ.ຄ່າຂົນສົ່ງ") }}</vs-th>
                  <vs-th>{{ $t("externalShipments.hal_service_fee") }}</vs-th>
                </vs-tr>
              </template>
              <template #tbody>
                <vs-tr :key="index" v-for="(item, index) in dashboard.top_branches">
                  <vs-td>{{ item.branch_name || ('#' + item.branch_id) }}</vs-td>
                  <vs-td>{{ FormatNumber(item.orders) }}</vs-td>
                  <vs-td>{{ FormatPrice(item.provider_freight) }}</vs-td>
                  <vs-td :style="{color: 'crimson'}">{{ FormatPrice(item.service_fee) }}</vs-td>
                </vs-tr>
              </template>
            </vs-table>
          </div>
        </div>
        <div class="column is-6">
          <div class="box">
            <h4 style="margin-bottom: 8px"><i class="fal fa-money-check-alt"></i> {{ $t("externalShipments.by_pay_type") }}</h4>
            <vs-table>
              <template #thead>
                <vs-tr>
                  <vs-th>{{ $t("externalShipments.type") }}</vs-th>
                  <vs-th>{{ $t("externalShipments.orders") }}</vs-th>
                  <vs-th>{{ $t("ລາຍການOrderພັດສະດຸ.ຄ່າຂົນສົ່ງ") }}</vs-th>
                  <vs-th>COD</vs-th>
                  <vs-th>{{ $t("externalShipments.hal_service_fee") }}</vs-th>
                </vs-tr>
              </template>
              <template #tbody>
                <vs-tr :key="index" v-for="(item, index) in dashboard.by_pay_type">
                  <vs-td>{{ la_pay_type[item.shipment_pay_type] || item.shipment_pay_type }}</vs-td>
                  <vs-td>{{ FormatNumber(item.count) }}</vs-td>
                  <vs-td>{{ FormatPrice(item.provider_freight) }}</vs-td>
                  <vs-td>{{ FormatPrice(item.cod) }}</vs-td>
                  <vs-td :style="{color: 'crimson'}">{{ FormatPrice(item.service_fee) }}</vs-td>
                </vs-tr>
              </template>
            </vs-table>

            <h4 style="margin: 16px 0 8px"><i class="fal fa-shipping-fast"></i> {{ $t("externalShipments.by_provider") }}</h4>
            <vs-table>
              <template #thead>
                <vs-tr>
                  <vs-th>{{ $t("externalShipments.provider") }}</vs-th>
                  <vs-th>{{ $t("externalShipments.orders") }}</vs-th>
                  <vs-th>{{ $t("ລາຍການOrderພັດສະດຸ.ຄ່າຂົນສົ່ງ") }}</vs-th>
                  <vs-th>{{ $t("externalShipments.hal_service_fee") }}</vs-th>
                </vs-tr>
              </template>
              <template #tbody>
                <vs-tr :key="index" v-for="(item, index) in dashboard.by_provider">
                  <vs-td>{{ item.provider_name || ('#' + item.provider_id) }}</vs-td>
                  <vs-td>{{ FormatNumber(item.orders) }}</vs-td>
                  <vs-td>{{ FormatPrice(item.provider_freight) }}</vs-td>
                  <vs-td :style="{color: 'crimson'}">{{ FormatPrice(item.service_fee) }}</vs-td>
                </vs-tr>
              </template>
            </vs-table>
          </div>
        </div>
      </div>
    </div>
    <Loading v-if="isLoading"/>
  </div>
</template>

<script>
import Loading from '../../Loading';
import moment from 'moment';
import DatePicker from "vue2-datepicker";
import ExternalDailyBarChart from '../../../components/Chart/ExternalDailyBarChart';

export default {
  components: {
    Loading,
    DatePicker,
    ExternalDailyBarChart,
  },

  data() {
    return {
      dashboard: null,
      isLoading: true,
      exporting: false,
      fromDate: moment(new Date()).subtract(29, 'days').format('YYYY-MM-DD'),
      toDate: moment(new Date()).format('YYYY-MM-DD'),

      statusTiles: [
        {key: 'pending', label: this.$t('externalShipments.status_pending'), icon: 'fal fa-clock', color: '#F6CA07'},
        {key: 'processing', label: this.$t('externalShipments.status_processing'), icon: 'fal fa-shipping-timed', color: '#2EA8D9'},
        {key: 'success', label: this.$t('ລາຍການOrderພັດສະດຸ.ຈັດສົ່ງສຳເລັດ'), icon: 'fal fa-check-circle', color: '#2DCE89'},
        {key: 'cancelled', label: this.$t('externalShipments.status_cancelled'), icon: 'fal fa-times-circle', color: '#E11F26'},
      ],

      la_pay_type: {
        'origin_freight_fees': this.$t('ລາຍການOrderພັດສະດຸ.ເກັບເງິນຕົ້ນທາງ'),
        'destination_freight_fees': this.$t('externalShipments.pay_type_destination_cdc'),
        'origin_freight_fees_cod': this.$t('ລາຍການOrderພັດສະດຸ.ເກັບເງິນຕົ້ນທາງ ( COD )'),
        'destination_freight_fees_cod': this.$t('ລາຍການOrderພັດສະດຸ.ເກັບເງິນປາຍທາງ ( COD + CDC )'),
      },
    };
  },

  computed: {
    dailyLabels() {
      return (this.dashboard?.daily || []).map((d) => moment(d.date).format('DD/MM'));
    },
    dailyOrders() {
      return (this.dashboard?.daily || []).map((d) => d.orders);
    },
    dailyFees() {
      return (this.dashboard?.daily || []).map((d) => d.service_fee);
    },
  },

  methods: {
    FetchDashboard() {
      this.isLoading = true;
      this.$axios.get('external-shipments/dashboard', {
        params: {from: this.fromDate, to: this.toDate},
      }).then((res) => {
        this.dashboard = res.data;
        this.isLoading = false;
      }).catch(() => {
        this.isLoading = false;
      });
    },
    statusCount(group) {
      const row = (this.dashboard?.by_status_group || []).find((g) => g.group === group);
      return row ? row.count : 0;
    },
    ExportReport() {
      // Full dashboard workbook (Summary / Daily / Status / Pay Types / Branches / Providers).
      this.exporting = true;
      this.$axios.get('external-shipments/dashboard/export', {
        params: {from: this.fromDate, to: this.toDate},
        responseType: 'blob',
      }).then((response) => {
        const fileUrl = window.URL.createObjectURL(new Blob([response.data]));
        const fileLink = document.createElement('a');
        fileLink.href = fileUrl;
        fileLink.setAttribute('download',
            this.$t('externalShipments.dashboard_export_file_name', {startDate: this.fromDate, endDate: this.toDate}) + '.xlsx');
        document.body.appendChild(fileLink);
        fileLink.click();
        document.body.removeChild(fileLink);
        this.exporting = false;
      }).catch(() => {
        this.exporting = false;
      });
    },
    GoToOrders() {
      this.$router.push({name: 'external.orders'});
    },
    FormatNumber(num) {
      const p = Number(num || 0).toFixed(0);
      return p.split("").reverse().reduce(function (acc, ch, i) {
        return ch === "-" ? acc : ch + (i && !(i % 3) ? "," : "") + acc;
      }, "");
    },
    FormatPrice(num) {
      var p = (num || 0).toFixed(2).split(".");
      return p[0].split("").reverse().reduce(function (acc, num, i) {
        return num == "-" ? acc : num + (i && !(i % 3) ? "," : "") + acc;
      }, "") + ' ' + this.$t('common.ກີບ');
    },
    disabledAfterToday(date) {
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      return date > today;
    },
  },

  created() {
    this.FetchDashboard();
  },
}
</script>

<style lang="scss" scoped>
.card-parent {
  display: flex;
  align-items: center;

  .card-left-section {
    margin-right: 12px;

    .card-box {
      width: 48px;
      height: 48px;
      border-radius: 8px;
      background: #E11F26;
      color: white;
      display: flex;
      align-items: center;
      justify-content: center;
      font-size: 20px;
    }
  }

  .card-right-section {
    h2 {
      font-size: 0.85rem;
      color: #666;
      margin-bottom: 2px;
    }

    h1 {
      font-size: 1.35rem;
      font-weight: 700;
    }
  }
}
</style>
